import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/* ---------------------------------- Panel --------------------------------- */

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  hover?: boolean;
  padded?: boolean;
}

export function Panel({ hover, padded = true, className, children, ...rest }: PanelProps) {
  return (
    <div
      className={cn('panel', padded && 'p-4 sm:p-5', hover && 'panel-hover', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function PanelHeader({
  title,
  subtitle,
  action,
  icon,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-4 flex items-start justify-between gap-3', className)}>
      <div className="flex items-start gap-3">
        {icon ? <div className="mt-0.5 text-agave-400">{icon}</div> : null}
        <div>
          <h3 className="text-base font-semibold text-white sm:text-lg">{title}</h3>
          {subtitle ? <p className="mt-0.5 text-sm text-ink-300">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

/* ---------------------------------- Badge --------------------------------- */

export type BadgeTone = 'neutral' | 'agave' | 'gold' | 'crimson' | 'sky' | 'violet';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-white/8 text-ink-100 border-white/10',
  agave: 'bg-agave-500/15 text-agave-300 border-agave-500/30',
  gold: 'bg-gold-500/15 text-gold-300 border-gold-500/30',
  crimson: 'bg-crimson-500/15 text-crimson-500 border-crimson-500/30',
  sky: 'bg-sky-450/15 text-sky-450 border-sky-450/30',
  violet: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ---------------------------------- Stat ---------------------------------- */

export function Stat({
  label,
  value,
  hint,
  tone = 'default',
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'positive' | 'negative';
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[11px] font-medium uppercase tracking-wider text-ink-400">{label}</div>
      <div
        className={cn(
          'numeric mt-1 truncate text-lg font-semibold',
          tone === 'positive' && 'text-agave-300',
          tone === 'negative' && 'text-crimson-500',
          tone === 'default' && 'text-white',
        )}
      >
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-xs text-ink-400">{hint}</div> : null}
    </div>
  );
}

/* -------------------------------- Progress -------------------------------- */

export function Progress({
  value,
  tone = 'agave',
  className,
  label,
}: {
  value: number;
  tone?: 'agave' | 'gold';
  className?: string;
  label?: string;
}) {
  const pct = Math.min(Math.max(value, 0), 1) * 100;
  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-full bg-white/8', className)}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500',
          tone === 'agave' && 'bg-gradient-to-r from-agave-500 to-agave-300',
          tone === 'gold' && 'bg-gradient-to-r from-gold-500 to-gold-300',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* -------------------------------- Spinner --------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block size-5 animate-spin rounded-full border-2 border-white/20 border-t-agave-400',
        className,
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

/* -------------------------------- Skeleton -------------------------------- */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-white/6', className)} />;
}

/* --------------------------------- Avatar --------------------------------- */

function hashHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

export function Avatar({
  seed,
  label,
  size = 36,
  className,
}: {
  seed: string;
  label?: string;
  size?: number;
  className?: string;
}) {
  const hue = hashHue(seed);
  const initials = (label ?? seed).slice(0, 2).toUpperCase();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-1 ring-white/15',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        backgroundImage: `linear-gradient(140deg, hsl(${hue} 62% 42%), hsl(${(hue + 60) % 360} 58% 24%))`,
      }}
      aria-hidden
    >
      {initials}
    </span>
  );
}

/* ---------------------------------- Kbd ----------------------------------- */

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-md border border-white/12 bg-white/6 px-1.5 py-0.5 font-mono text-[11px] text-ink-200">
      {children}
    </kbd>
  );
}

/* ------------------------------- Divider ---------------------------------- */

export function Divider({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent',
        className,
      )}
    />
  );
}
